/**
 * The Readout view (BUILD-SPEC-2 §2): the hero experience. Drop a typed pay
 * stub, W-2, or 1040 and get an instant private readout, parsed entirely on the
 * device. Extraction is deterministic and anchored (never inferred); every value
 * is shown with its confidence and a needs-review flag, and the user confirms
 * before anything flows into My Situation. Nothing is uploaded — the strict
 * CSP keeps `connect-src 'none'`, so the browser physically cannot send the
 * document anywhere.
 */
import { el, clear, option } from "./dom";
import { field } from "./form";
import { applyToSituation, replacementNote } from "../readout/toSituation";
import {
  extractTextFromFile,
  PDF_PASSWORD_REQUIRED,
  type TextExtractor,
} from "../readout/extractText";
import { importProfile, isEncrypted, readFileText } from "../profile/portable";
import {
  diffLedger,
  importLedger,
  isEncryptedLedger,
  isLedgerFile,
  type LedgerSnapshot,
} from "../profile/ledger";
import { renderLedgerDiff } from "./ledgerView";
import { buildReport } from "../readout/report";
/** Loaded with the first document, not with the shell (see readout/engine.ts). */
type ReadoutEngine = typeof import("../readout/engine");
let engine: ReadoutEngine | null = null;
async function loadEngine(): Promise<ReadoutEngine> {
  engine ??= await import("../readout/engine");
  return engine;
}
import type { PlanParameters } from "../readout/checks";
import { citationLink } from "./resultCard";
import { renderDeadline, todayIso } from "./deadline";
import type {
  CheckOutcome,
  ExtractedField,
  ExtractionResult,
  ReadoutAnswer,
} from "../readout/types";
import { SituationStore } from "../profile/situation";
import type { BundledData } from "../data/browser";
import type { FilingStatus } from "../data/schemas";

const FILING_LABELS: Record<FilingStatus, string> = {
  single: "Single",
  married_jointly: "Married filing jointly",
  married_separately: "Married filing separately",
  head_of_household: "Head of household",
  qualifying_surviving_spouse: "Qualifying surviving spouse",
};

/** How each check family is labeled on screen (SPEC-4-readout-v2 §4). The label
 * carries the confidence: "the document disagrees with itself" is not the same
 * claim as "this is unusual". */
const CHECK_KIND_LABEL = {
  arithmetic: "the document's own arithmetic",
  "plan-math": "against the plan details you entered",
  rule: "against a published rule",
  anomaly: "unusual, not necessarily wrong",
} as const;

const CONFIDENCE_LABEL = {
  high: "high confidence",
  "needs-review": "review",
  low: "low confidence",
} as const;

/**
 * A dropped `.json` is a previously-saved situation to restore, not a tax
 * document to parse — no document the extractors read is JSON, so this split is
 * unambiguous and keeps the extraction path untouched.
 */
function isSituationFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".json") || file.type === "application/json";
}

export interface RenderReadoutOptions {
  container: HTMLElement;
  navigate: (id: string | null) => void;
  profile: SituationStore;
  /** Bundled datasets, so the summary can show the tax rate and the next step. */
  data?: BundledData | null;
  /** Injectable for tests; defaults to the real on-device extractor. */
  extractor?: TextExtractor;
}

function confidenceBadge(field: ExtractedField): HTMLElement {
  const cls =
    field.confidence === "high"
      ? "badge badge--good"
      : field.confidence === "low"
        ? "badge badge--warn"
        : "badge badge--soon";
  return el("span", { class: cls, text: CONFIDENCE_LABEL[field.confidence] });
}

/** A friendly, deterministic plain-English line from the confirmed fields. */
function summaryLine(fields: ExtractedField[]): string {
  const income = fields.find((f) => f.target === "annualIncome");
  const status = fields.find((f) => f.target === "filingStatus");
  const parts: string[] = [];
  if (income && typeof income.value === "number") {
    parts.push(
      `income of ${income.value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}`,
    );
  }
  if (status && typeof status.value === "string") {
    parts.push(`filing as ${FILING_LABELS[status.value as FilingStatus].toLowerCase()}`);
  }
  return parts.length > 0
    ? `Here's where you stand: ${parts.join(", ")}.`
    : "Your values are ready to review.";
}

/**
 * The plan parameters a check may read, from the answers the reader has already
 * given elsewhere. Absent stays absent: a plan-math check with no plan behind it
 * says nothing rather than assuming one.
 */
function planFrom(profile: SituationStore): PlanParameters | undefined {
  const deductible = profile.get("planDeductible");
  const oopMax = profile.get("planOopMax");
  if (deductible === undefined && oopMax === undefined) return undefined;
  return { deductible, oopMax };
}

export function renderReadout(opts: RenderReadoutOptions): void {
  const { container, navigate, profile } = opts;
  const data = opts.data ?? null;
  const extractor = opts.extractor ?? extractTextFromFile;
  clear(container);
  document.title = "The Readout · enklayve";

  const back = el(
    "button",
    { type: "button", class: "btn btn--ghost back-link", on: { click: () => navigate(null) } },
    "← Home",
  );
  const head = el(
    "div",
    { class: "tile-head" },
    back,
    el("h1", { class: "tile-title", text: "The Readout" }),
    el("p", {
      class: "tile-desc",
      text: "Drop a pay stub, W-2, 1040, 1099, 1095-A, 1098 mortgage statement, FAFSA Submission Summary, health plan Explanation of Benefits, itemized medical bill, or benefits determination notice. You get what it says, what looks worth asking about, what you may be owed, and what to do next — parsed on your device, never uploaded.",
    }),
  );

  const status = el("p", { class: "readout-status", attrs: { "aria-live": "polite" }, text: "" });
  const resultRegion = el("div", { class: "readout-result", attrs: { "aria-live": "polite" } });

  const fileInput = el("input", {
    type: "file",
    class: "readout-file",
    name: "readout-file",
    attrs: {
      accept:
        ".pdf,.docx,.txt,.text,.png,.jpg,.jpeg,.webp,.bmp,.gif,.tif,.tiff,.json,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/json,image/*",
      "aria-label": "Choose a document to read, or a saved situation (.json) to restore",
    },
    on: {
      change: (e) => {
        const f = (e.target as HTMLInputElement).files?.[0];
        if (f) void handleFile(f);
      },
    },
  });

  const dropzone = el(
    "label",
    {
      class: "readout-dropzone readout-dropzone--live",
      on: {
        dragover: (e) => {
          e.preventDefault();
          dropzone.classList.add("is-dragover");
        },
        dragleave: () => dropzone.classList.remove("is-dragover"),
        drop: (e) => {
          e.preventDefault();
          dropzone.classList.remove("is-dragover");
          const f = (e as DragEvent).dataTransfer?.files?.[0];
          if (f) void handleFile(f);
        },
      },
    },
    el("span", { class: "readout-dropzone-icon", attrs: { "aria-hidden": "true" }, text: "⤓" }),
    el("span", { class: "readout-dropzone-title", text: "Drop a file here, or choose one" }),
    el("span", {
      class: "readout-dropzone-sub",
      text: "PDF, Word (.docx), text, or a scanned image (PNG/JPG). A scan — a photo or a scanner's output, whether it arrives as an image or inside a PDF — is read on-device with OCR and flagged for review. Already have a saved situation? Drop its .json here to restore it.",
    }),
    fileInput,
  );

  /**
   * Every document read in this tab, in memory only. It is what makes the §5
   * cross-document checks (EOB × medical bill) possible, and it is gone the
   * moment the tab is closed — nothing about a document is persisted anywhere.
   */
  const session: ExtractionResult[] = [];

  async function handleFile(file: File): Promise<void> {
    clear(resultRegion);
    // A saved enklayve situation (.json) is a restore, not a document to parse.
    if (isSituationFile(file)) {
      await handleRestore(file);
      return;
    }
    await readDocument(file);
  }

  /**
   * Read a dropped document, asking for a password if the file needs one.
   *
   * A locked PDF is the likeliest way a real tax document fails to open here:
   * payroll providers and banks deliver W-2s, 1099s and statements encrypted,
   * often with the last four of an SSN as the password. Telling the reader to
   * go unlock it in another program was honest and was the wrong answer — the
   * decryption happens in pdf.js, on this device, which is the one thing this
   * page is for.
   */
  async function readDocument(file: File, password?: string): Promise<void> {
    status.textContent = "Reading on your device…";
    try {
      const [text, { extractDocument }] = await Promise.all([
        extractor(file, password),
        loadEngine(),
      ]);
      const result = extractDocument(text);
      if (result.recognized) session.push(result);
      status.textContent = "";
      renderResult(result);
    } catch (err) {
      if ((err as Error).name === PDF_PASSWORD_REQUIRED) {
        status.textContent = "";
        renderPdfUnlock(file, (err as Error).message);
        return;
      }
      status.textContent = (err as Error).message;
    }
  }

  /**
   * Ask for a locked PDF's password, and read it with what the reader types.
   *
   * The passphrase row the encrypted-profile path already uses, for the same
   * reason: one shape for "this file needs a secret", so a reader meets the
   * same control twice rather than two inventions. The value lives in the
   * input and in the argument to one call — never written to storage, never
   * put in the fragment, never logged, and gone with the page.
   */
  function renderPdfUnlock(file: File, why: string): void {
    clear(resultRegion);
    const pass = el("input", {
      type: "password",
      class: "portable-pass",
      name: "pdf-password",
      attrs: {
        placeholder: "PDF password",
        autocomplete: "off",
        "aria-label": "Password for this PDF",
      },
    });
    const open = el("button", {
      type: "button",
      class: "btn btn--accent",
      text: "Unlock & read",
      on: {
        click: () => {
          void readDocument(file, pass.value);
        },
      },
    });
    resultRegion.append(
      el(
        "div",
        { class: "readout-fields" },
        el("p", { class: "readout-note", attrs: { "aria-live": "polite" }, text: why }),
        el("div", { class: "portable-actions" }, pass, open),
      ),
    );
    pass.focus();
  }

  /**
   * Restore a saved file. A `.json` here is one of three things: a saved
   * situation, a Standing Ledger snapshot, or an encrypted envelope of either.
   * The format id in the file decides, so the three paths never guess.
   */
  async function handleRestore(file: File): Promise<void> {
    status.textContent = "";
    try {
      const text = await readFileText(file);
      if (isLedgerFile(text)) {
        showLedger(await importLedger(text));
        return;
      }
      if (isEncrypted(text)) {
        renderUnlock(text, isEncryptedLedger(text));
        return;
      }
      await importProfile(profile, text);
      renderRestored();
    } catch (e) {
      status.textContent = `That .json isn't a saved enklayve file: ${(e as Error).message}`;
    }
  }

  /** Today, as the default clock for the deadline view. Displayed and editable —
   * the figures above it are pure; only "days remaining" needs a date. */
  let ledgerAsOf = todayIso();

  /**
   * Recompute the answers in a dropped ledger against the data bundled today and
   * show the diff. The snapshot's situation is loaded into a **temporary** store,
   * so viewing the diff never overwrites My Situation — that stays a deliberate
   * button press.
   */
  function showLedger(snapshot: LedgerSnapshot): void {
    clear(resultRegion);
    const temp = new SituationStore();
    temp.load(snapshot.situation);
    const current = buildReport(temp, data);
    const shards = (data?.manifest.datasets ?? []).map((d) => ({
      id: d.id,
      version: d.version,
      effectiveYear: d.effectiveYear,
    }));
    renderLedgerDiff(resultRegion, {
      snapshot,
      diff: diffLedger(snapshot, current, shards),
      asOf: ledgerAsOf,
      locale: "en-US",
      onRestore: () => {
        profile.load(snapshot.situation);
        renderRestored();
      },
      onAsOfChange: (next) => {
        ledgerAsOf = next;
        showLedger(snapshot);
      },
    });
  }

  /** An encrypted situation needs its passphrase before it can be restored. */
  function renderUnlock(text: string, isLedger = false): void {
    clear(resultRegion);
    const pass = el("input", {
      type: "password",
      class: "portable-pass",
      name: "restore-passphrase",
      attrs: {
        placeholder: "Passphrase",
        autocomplete: "off",
        "aria-label": "Passphrase to open the encrypted file",
      },
    });
    const msg = el("p", {
      class: "readout-note",
      attrs: { "aria-live": "polite" },
      text: isLedger
        ? "That ledger is encrypted. Enter its passphrase to see what changed."
        : "That file is encrypted. Enter its passphrase to restore your situation.",
    });
    async function doUnlock(): Promise<void> {
      try {
        if (isLedger) {
          showLedger(await importLedger(text, pass.value.trim()));
          return;
        }
        await importProfile(profile, text, pass.value.trim());
        renderRestored();
      } catch (e) {
        msg.textContent = (e as Error).message;
      }
    }
    const unlock = el("button", {
      type: "button",
      class: "btn btn--accent",
      text: isLedger ? "Unlock & compare" : "Unlock & restore",
      on: {
        click: () => {
          void doUnlock();
        },
      },
    });
    resultRegion.append(
      el(
        "div",
        { class: "readout-fields" },
        msg,
        el("div", { class: "portable-actions" }, pass, unlock),
      ),
    );
  }

  /** Confirm a restore and show where the restored situation stands. */
  function renderRestored(): void {
    clear(resultRegion);
    const count = profile.entries().length;
    resultRegion.append(
      el(
        "section",
        { class: "readout-summary", attrs: { "aria-label": "Restored situation" } },
        el("p", {
          class: "readout-summary-line",
          text:
            count > 0
              ? `Restored your situation — ${count} value${count === 1 ? "" : "s"}.`
              : "Restored, but that file held no values.",
        }),
        standingBlock(),
        el("p", {
          class: "readout-note",
          text: "These prefill the matching tools. Everything stays in this browser tab and is cleared when you leave; nothing is uploaded.",
        }),
        el(
          "div",
          { class: "readout-actions" },
          el("button", {
            type: "button",
            class: "btn btn--accent",
            text: "My Readout Report →",
            on: { click: () => navigate("report") },
          }),
          el("button", {
            type: "button",
            class: "btn btn--ghost",
            text: "Read another file",
            on: {
              click: () => {
                fileInput.value = "";
                clear(resultRegion);
                status.textContent = "";
              },
            },
          }),
        ),
      ),
    );
  }

  function renderResult(result: ExtractionResult): void {
    // Set before the first call: `readDocument` awaits it with the text.
    const { labelFor, buildAnswer } = engine!;
    clear(resultRegion);

    for (const w of result.warnings) {
      resultRegion.append(
        el("p", {
          class: result.recognized ? "readout-note" : "readout-note readout-note--warn",
          text: w,
        }),
      );
    }

    if (result.fields.length === 0) {
      resultRegion.append(
        el(
          "div",
          { class: "readout-actions" },
          el("button", {
            type: "button",
            class: "btn btn--accent",
            text: "Enter your numbers in the budget →",
            on: { click: () => navigate(null) },
          }),
        ),
      );
      return;
    }

    const source = result.source === "ocr" ? "optical character recognition" : "the typed document";
    resultRegion.append(
      el("p", {
        class: "readout-detected",
        text: `Recognized a ${labelFor(result.kind)}${result.revision ? ` (${result.revision})` : ""}, read from ${source}.`,
      }),
    );
    if (result.citation) {
      resultRegion.append(
        el(
          "p",
          { class: "readout-cite" },
          el("span", { text: `Form: ${result.citation.sourceDocument} ` }),
          el(
            "a",
            {
              class: "cite-link",
              href: result.citation.sourceUrl,
              attrs: { rel: "noopener noreferrer", target: "_blank" },
            },
            "source",
          ),
        ),
      );
    }

    // An editable copy so the user confirms (and can correct) before anything is used.
    const working: ExtractedField[] = result.fields.map((f) => ({ ...f }));

    const list = el("div", { class: "tile-form readout-fields" });
    working.forEach((f, i) => {
      if (f.target === "filingStatus") {
        const sel = el(
          "select",
          {
            name: f.id,
            attrs: { "aria-label": f.label },
            on: {
              change: (e) => (working[i] = { ...f, value: (e.target as HTMLSelectElement).value }),
            },
          },
          ...(Object.keys(FILING_LABELS) as FilingStatus[]).map((s) =>
            option(s, FILING_LABELS[s], s === f.value),
          ),
        );
        list.append(wrapField(f, sel));
      } else if (typeof f.value === "string") {
        // A field the extractor could not resolve to a value: Form 1040's filing
        // status when the page shows the printed option list rather than which
        // box is checked. It carries no `target`, so it cannot reach My
        // Situation, and there is nothing here for the reader to correct —
        // offering a number box under the label "Filing status", which is what
        // the numeric branch below does with a string, would be a control that
        // does nothing dressed as one that does. It is shown, with its note
        // saying where to answer instead.
        list.append(wrapField(f, el("span", { class: "readout-field-static", text: f.value })));
      } else {
        const input = el("input", {
          type: "number",
          name: f.id,
          step: 1,
          value: typeof f.value === "number" ? f.value : "",
          attrs: { "aria-label": f.label, inputmode: "decimal" },
          on: {
            input: (e) => {
              // A cleared box means "not this one", not zero. Deleting a figure
              // the extractor got wrong is the natural way to say leave it out,
              // and `Number("")` is 0 — which is finite, so it used to be
              // confirmed and written. A reader who cleared the wages box and
              // pressed Confirm recorded an annual income of $0, which the
              // subsidy screeners and the EITC tile downstream believe.
              const raw = (e.target as HTMLInputElement).value.trim();
              const v = Number(raw);
              working[i] = { ...f, value: raw !== "" && Number.isFinite(v) ? v : "" };
            },
          },
        });
        list.append(wrapField(f, input));
      }
    });

    const confirm = el("button", {
      type: "button",
      class: "btn btn--accent",
      text: "Confirm and add to My Situation",
      on: {
        click: () => {
          const update = applyToSituation(profile, working);
          renderSummary(working, update.applied, replacementNote(update.replaced, "en-US"));
        },
      },
    });

    resultRegion.append(
      list,
      answerBlock(
        buildAnswer(result, {
          documents: session,
          noSurprises: data?.noSurprises() ?? undefined,
          windows: data?.enrollmentWindows() ?? undefined,
          // The clock is an input, shown beside the figure it produces.
          asOf: todayIso(),
          // Every field of `CheckContext`. `buildAnswer`'s own comment says a
          // check reading a field this call drops cannot fire and nothing says
          // so — and that fix went into `buildAnswer` while its one production
          // caller went on dropping `plan`, so the whole plan-math family had
          // never run in the app. Its unit cases passed throughout, because
          // each builds a context of its own.
          plan: planFrom(profile),
        }),
      ),
      el("div", { class: "readout-actions" }, confirm),
    );
  }

  /** One section of the four-part answer, or its one-line reason for being empty. */
  function answerSection(
    title: string,
    body: HTMLElement | null,
    emptyReason: string | undefined,
  ): HTMLElement {
    return el(
      "section",
      { class: "readout-answer-section" },
      // h2: the view's h1 is the Readout title, so these are its direct children.
      el("h2", { class: "readout-answer-title", text: title }),
      body ?? el("p", { class: "readout-answer-empty", text: emptyReason ?? "" }),
    );
  }

  /** One check that fired: the question, the arithmetic behind it, and who to ask. */
  function flagItem(flag: CheckOutcome): HTMLElement {
    return el(
      "li",
      { class: `readout-flag readout-flag--${flag.kind}` },
      el("p", { class: "readout-flag-q", text: flag.question }),
      el("p", { class: "readout-flag-detail", text: flag.detail }),
      el(
        "p",
        { class: "readout-flag-meta" },
        el("span", { class: "badge badge--soon", text: CHECK_KIND_LABEL[flag.kind] }),
        flag.fromOcr ? el("span", { class: "badge badge--warn", text: "read from a scan" }) : null,
        flag.citation ? citationLink(flag.citation) : null,
      ),
      el("p", { class: "readout-flag-ask", text: `Who to ask: ${flag.askWho}` }),
    );
  }

  /**
   * The four-part answer (SPEC-4-readout-v2 §2): what this says, what looks
   * wrong, what you may be owed, and what to do next. Same four sections in the
   * same order for every document kind — the shape is the product.
   */
  function answerBlock(answer: ReadoutAnswer): HTMLElement {
    const says =
      answer.says.length > 0
        ? el(
            "dl",
            { class: "readout-says" },
            ...answer.says.flatMap((s) => [
              el("dt", { text: s.label }),
              el("dd", { text: s.value, attrs: { "data-field": s.fieldId } }),
            ]),
          )
        : null;

    const flags =
      answer.flags.length > 0
        ? el("ul", { class: "readout-flags" }, ...answer.flags.map(flagItem))
        : null;

    const owed =
      answer.owed.length > 0
        ? el(
            "ul",
            { class: "readout-owed" },
            ...answer.owed.map((o) =>
              el(
                "li",
                { class: "readout-owed-item" },
                el("span", { text: `${o.label} ` }),
                citationLink(o.citation),
                " ",
                el("button", {
                  type: "button",
                  class: "btn btn--ghost",
                  text: "Open the tool →",
                  on: { click: () => navigate(o.tileId) },
                }),
              ),
            ),
          )
        : null;

    const next =
      answer.next.length > 0
        ? el(
            "ol",
            { class: "readout-next" },
            ...answer.next.map((n) =>
              el(
                "li",
                { class: "readout-next-item" },
                el("span", { text: n.label }),
                n.channel
                  ? el("a", {
                      class: "cite-link",
                      href: n.channel.url,
                      text: n.channel.label,
                      attrs: { rel: "noopener noreferrer", target: "_blank" },
                    })
                  : null,
                // Through the one path every deadline on the site takes, so its
                // citation and the clock it was read against render with it.
                n.deadline
                  ? renderDeadline(n.deadline, { asOf: todayIso(), locale: "en-US" })
                  : null,
              ),
            ),
          )
        : null;

    return el(
      "section",
      { class: "readout-answer", attrs: { "aria-label": "What this document means" } },
      answerSection("What this says", says, answer.emptyReasons.says),
      answerSection("What looks wrong", flags, answer.emptyReasons.flags),
      answerSection("What you may be owed", owed, answer.emptyReasons.owed),
      answerSection("What to do next, by when", next, answer.emptyReasons.next),
      el("p", {
        class: "readout-answer-caveat",
        text: "Every line above is a question to ask, not a verdict. Nothing here is a determination, and nothing has been sent anywhere.",
      }),
    );
  }

  function wrapField(f: ExtractedField, control: HTMLElement): HTMLElement {
    const labelRow = el(
      "span",
      { class: "readout-field-label" },
      el("span", { text: f.label }),
      confidenceBadge(f),
    );
    const wrapped = field(f.label, control);
    // Replace the plain label with one that carries the confidence badge.
    const existingLabel = wrapped.querySelector("label");
    if (existingLabel)
      existingLabel.replaceWith(el("span", { class: "field-label-wrap" }, labelRow));
    if (f.note) wrapped.append(el("span", { class: "readout-field-note", text: f.note }));
    return wrapped;
  }

  /**
   * The §2.3 payoff, composed from the same engine the Readout Report uses (so
   * the figures match and nothing is duplicated): the effective tax rate and
   * annual take-home, plus the single next right step from My Plan. Returns null
   * when there's no data or no income yet, so the summary degrades gracefully.
   */
  function standingBlock(): HTMLElement | null {
    if (!data) return null;
    const model = buildReport(profile, data);
    const grid = el("dl", { class: "readout-standing" });
    if (model.hasIncomeData) {
      const snap = model.sections.find((s) => s.title === "Snapshot");
      for (const label of ["Effective tax rate", "Annual take-home"]) {
        const line = snap?.lines.find((l) => l.label === label);
        if (line) grid.append(el("dt", { text: label }), el("dd", { text: line.value }));
      }
    }
    const planSection = model.sections.find((s) => s.title.startsWith("My Plan"));
    const stepTitle = planSection?.lines.find((l) => l.label === "Current step")?.value;
    const action = planSection?.lines.find((l) => l.label === "Next action")?.value;

    if (grid.childElementCount === 0 && !stepTitle) return null;
    return el(
      "div",
      { class: "readout-standing-wrap" },
      grid.childElementCount > 0 ? grid : null,
      stepTitle
        ? el(
            "p",
            { class: "readout-next-step" },
            el("strong", { text: "Your next right step: " }),
            el("span", { text: action ? `${stepTitle}. ${action}` : stepTitle }),
          )
        : null,
    );
  }

  function renderSummary(fields: ExtractedField[], applied: number, replaced = ""): void {
    clear(resultRegion);
    resultRegion.append(
      el(
        "section",
        { class: "readout-summary", attrs: { "aria-label": "Your readout" } },
        el("p", { class: "readout-summary-line", text: summaryLine(fields) }),
        standingBlock(),
        el("p", {
          class: "readout-note",
          text:
            applied > 0
              ? `Added ${applied} value${applied === 1 ? "" : "s"} to My Situation (provenance: from a document) — they prefill the matching tools. Open the report to see where you stand and, if you like, save a private copy you can restore later. Everything stays in this browser tab and is cleared when you leave; nothing is uploaded.`
              : "These values are informational: review them above and carry them into the matching tile. Nothing was changed in My Situation.",
        }),
        // A second document in the same session can land on a slot the first
        // one filled. Saying so is the whole point: the profile keeps one value
        // per field, and the reader is the only one who knows which they meant.
        replaced ? el("p", { class: "readout-note readout-note--replaced", text: replaced }) : null,
        el(
          "div",
          { class: "readout-actions" },
          el("button", {
            type: "button",
            class: "btn btn--accent",
            text: "My Readout Report →",
            on: { click: () => navigate("report") },
          }),
          el("button", {
            type: "button",
            class: "btn btn--ghost",
            text: "Read another document",
            on: {
              click: () => {
                fileInput.value = "";
                clear(resultRegion);
                status.textContent = "";
              },
            },
          }),
        ),
      ),
    );
  }

  const privacy = el("p", {
    class: "readout-privacy",
    text: "Everything here is computed on your device. The Content-Security-Policy keeps connect-src 'none', so your documents cannot leave the page.",
  });

  container.append(el("article", { class: "tile" }, head, dropzone, status, resultRegion, privacy));
}
